import { ChartData } from 'chart.js'
import { useMemo } from 'react'
import { useBreakpoints } from 'styles'
import { Timestamp } from 'firebase/firestore'

import { StackedBarGraph } from 'components/graph'
import MonthTally from '../month-tally'
import { Contract, Feature, PoratalSiteInfo } from 'schema/contract'
import theme from 'styles/theme'
import { formatedDate } from 'utils'
import {
  BAR_GRAPH_OPTIONS,
  GRAPH_PLUGINS,
} from '../../../pages/contract/constants/graph'
import {
  createBarGraphData,
  createGraphLabel,
} from '../../../pages/contract/logic/graph'
import { getDay, parse, subDays } from 'date-fns'

const AtHomeFavoriteGraph = ({
  contract,
}: {
  contract?: Contract | Feature
}) => {
  const breakpoint = useBreakpoints()

  const labels = useMemo(() => {
    return createGraphLabel(breakpoint)
  }, [breakpoint])

  const graphData = useMemo(() => {
    if (!contract?.athome?.favoriteCount) {
      return []
    }

    // favoriteCount は当日時点のお気に入り数が表示される（差分でない）
    // SP版は1週間のデータの合計値を表示する仕組みになっているため、合計値が大きくなってしまう
    let favoriteCount: PoratalSiteInfo[] = contract?.athome?.favoriteCount || []
    favoriteCount = favoriteCount.map((data) => {
      // PCの場合はそのまま返す
      if (breakpoint === 'pc') {
        return data
      }
      const formatDate = formatedDate(data.date, 'yyyy/MM/dd')
      const parsedDate = parse(formatDate, 'yyyy/MM/dd', new Date())
      // SPの場合は月曜日のデータのみを返す
      const day = getDay(parsedDate)
      if (day !== 1) {
        return {
          ...data,
          quantity: 0,
        }
      } else {
        return data
      }
    })

    return createBarGraphData(favoriteCount, breakpoint)
  }, [breakpoint, contract?.athome?.favoriteCount])

  // 直近30日のお気に入り登録数（日次増減の積算）を計算
  const favoriteIncrease = useMemo(() => {
    if (
      !contract?.athome?.favoriteCount ||
      contract.athome.favoriteCount.length < 2
    ) {
      return 0
    }

    // データを日付順（古い順→新しい順）に並び替え
    const sortedData = [...contract.athome.favoriteCount].sort((a, b) => {
      const dateA =
        typeof a.date === 'object' && '_seconds' in a.date
          ? a.date._seconds
          : (a.date as any).seconds
      const dateB =
        typeof b.date === 'object' && '_seconds' in b.date
          ? b.date._seconds
          : (b.date as any).seconds
      return dateA - dateB
    })

    // 30日前の日付を取得（その日の終わり）
    const targetDate = subDays(new Date(), 30)
    targetDate.setHours(23, 59, 59, 999)

    // 直近30日間のデータのみ抽出
    const recentData = sortedData.filter((data) => {
      let dataDate: Date

      // 異なる形式のタイムスタンプを統一的にDate型に変換する
      if (data.date instanceof Timestamp) {
        dataDate = data.date.toDate()
      } else if (typeof data.date === 'object' && '_seconds' in data.date) {
        dataDate = new Date(data.date._seconds * 1000)
      } else {
        dataDate = new Date((data.date as any).seconds * 1000)
      }
      return dataDate >= targetDate
    })

    if (recentData.length < 2) {
      return 0
    }

    // 各日の前日比増減を合計
    let totalIncrease = 0
    for (let i = 1; i < recentData.length; i++) {
      const currentData = recentData[i]
      const previousData = recentData[i - 1]

      if (currentData && previousData) {
        const increase = currentData.quantity - previousData.quantity
        totalIncrease += increase
      }
    }

    return totalIncrease
  }, [contract?.athome?.favoriteCount])

  const data: ChartData = {
    labels,
    datasets: [
      {
        label: '',
        data: graphData,
        backgroundColor: theme.GRAPH_COLOR_05,
        borderWidth: 0,
      },
    ],
  }

  return (
    <>
      <StackedBarGraph
        data={data}
        options={{
          ...BAR_GRAPH_OPTIONS,
          plugins: {
            ...BAR_GRAPH_OPTIONS.plugins,
            legend: {
              ...BAR_GRAPH_OPTIONS.plugins?.legend,
              display: false,
            },
          },
        }}
        plugins={GRAPH_PLUGINS('回数')}
      />
      <MonthTally
        list={[
          {
            label: '直近30日の登録数',
            value: favoriteIncrease,
            unit: '回',
          },
        ]}
      />
    </>
  )
}

export default AtHomeFavoriteGraph
